import {
  BAD_REQUEST,
  CONFLICT,
  FORBIDDEN,
  NOT_FOUND,
  REQUEST_TIMEOUT,
  UNAUTHORIZED,
  UNPROCESSABLE_ENTITY,
} from '@app-builder/utils/http/http-status-codes';
import type * as Oazapfts from '@oazapfts/runtime';
import * as z from 'zod/v4';

export function isHttpError(error: unknown): error is Oazapfts.HttpError {
  return error instanceof Error && 'status' in error;
}

export function isStatusConflictHttpError(error: unknown): error is Oazapfts.HttpError {
  return isHttpError(error) && error.status === CONFLICT;
}

export function isStatusBadRequestHttpError(error: unknown): error is Oazapfts.HttpError {
  return isHttpError(error) && error.status === BAD_REQUEST;
}

export function isNotFoundHttpError(error: unknown): error is Oazapfts.HttpError {
  return isHttpError(error) && error.status === NOT_FOUND;
}

export function isUnprocessableEntityHttpError(error: unknown): error is Oazapfts.HttpError {
  return isHttpError(error) && error.status === UNPROCESSABLE_ENTITY;
}

export function isUnauthorizedHttpError(error: unknown): error is Oazapfts.HttpError {
  return isHttpError(error) && error.status === UNAUTHORIZED;
}

export function isForbiddenHttpError(error: unknown): error is Oazapfts.HttpError {
  return isHttpError(error) && error.status === FORBIDDEN;
}

export function isRequestTimeoutHttpError(error: unknown): error is Oazapfts.HttpError {
  return isHttpError(error) && error.status === REQUEST_TIMEOUT;
}

export class RequestTimeoutError extends Error {
  override name = 'RequestTimeoutError';
}

const marbleErrorSchema = z.object({
  error_code: z.string().optional(),
  message: z.string(),
});
export function isMarbleError(error: Oazapfts.HttpError): error is Omit<Oazapfts.HttpError, 'data'> & {
  data: z.infer<typeof marbleErrorSchema>;
} {
  const result = marbleErrorSchema.safeParse(error.data);
  return result.success;
}

export function marbleApiErrorMessage(error: unknown): string | null {
  if (!isHttpError(error) || !isMarbleError(error) || !error.data.message) return null;
  return error.data.message;
}

export function forbiddenApiMessage(error: unknown): string | null {
  if (!isForbiddenHttpError(error)) return null;
  return marbleApiErrorMessage(error) ?? 'Forbidden';
}
